import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Alert,
  Divider,
  Chip
} from '@mui/joy';
import { FaUser, FaPlus, FaSearch } from 'react-icons/fa';
import { IconWrapper } from '../components/IconWrapper';
import CustomerCreationForm from '../components/CustomerCreationForm';
import { useCustomer } from '../hooks/useCustomer';

const CustomerTestPage: React.FC = () => {
  const { searchCustomers, loading, error } = useCustomer();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [createdCustomer, setCreatedCustomer] = useState<any>(null);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    try {
      const results = await searchCustomers(searchQuery);
      setSearchResults(results);
    } catch (err) {
      console.error('Search failed:', err);
    }
  };

  const handleCustomerCreated = (customer: any) => {
    setCreatedCustomer(customer);
    setShowCreateForm(false);
    // Refresh search results
    if (searchQuery) {
      handleSearch();
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography level="h2" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconWrapper icon={FaUser} />
        Customer Management Test
      </Typography>

      {error && (
        <Alert color="danger" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Search Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography level="h4" sx={{ mb: 2 }}>
            <IconWrapper icon={FaSearch} />
            Search Customers
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, phone, or email..."
              style={{
                flex: 1,
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
            <Button
              onClick={handleSearch}
              loading={loading}
              startDecorator={<IconWrapper icon={FaSearch} />}
            >
              Search
            </Button>
          </Stack>

          {searchResults.length > 0 && (
            <Box>
              <Typography level="title-sm" sx={{ mb: 1 }}>
                Search Results ({searchResults.length})
              </Typography>
              <Stack spacing={1}>
                {searchResults.map((customer, index) => (
                  <Card key={index} variant="outlined" size="sm">
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography level="title-sm">
                            {customer.name}
                            <Chip 
                              size="sm" 
                              color={customer.source === 'api' ? 'primary' : 'success'} 
                              sx={{ ml: 1 }}
                            >
                              {customer.source}
                            </Chip>
                          </Typography>
                          <Typography level="body-xs" color="neutral">
                            {customer.phone} • {customer.email || 'No email'}
                          </Typography>
                          <Typography level="body-xs" color="neutral">
                            {customer.address}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Create Customer Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography level="h4">
              <IconWrapper icon={FaPlus} />
              Create New Customer
            </Typography>
            <Button
              variant={showCreateForm ? "outlined" : "solid"}
              onClick={() => setShowCreateForm(!showCreateForm)}
              startDecorator={<IconWrapper icon={showCreateForm ? FaUser : FaPlus} />}
            >
              {showCreateForm ? 'Cancel' : 'Create Customer'}
            </Button>
          </Box>

          {showCreateForm && (
            <CustomerCreationForm
              onCustomerCreated={handleCustomerCreated}
              onCancel={() => setShowCreateForm(false)}
            />
          )}

          {createdCustomer && (
            <Alert color="success" sx={{ mt: 2 }}>
              <Typography level="title-sm">Customer Created Successfully!</Typography>
              <Typography level="body-sm">
                {createdCustomer.name} ({createdCustomer.phone}) - Source: {createdCustomer.source}
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Instructions */}
      <Card>
        <CardContent>
          <Typography level="h4" sx={{ mb: 2 }}>
            How to Test
          </Typography>
          <Stack spacing={1}>
            <Typography level="body-sm">
              1. <strong>Search:</strong> Try searching for existing customers by name, phone, or email
            </Typography>
            <Typography level="body-sm">
              2. <strong>Create:</strong> Click "Create Customer" to add a new customer to the local database
            </Typography>
            <Typography level="body-sm">
              3. <strong>Verify:</strong> After creating a customer, search again to see it appears in results
            </Typography>
            <Typography level="body-sm">
              4. <strong>Source:</strong> Customers show their source (API or local) with colored chips
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CustomerTestPage; 